import React, { useState } from "react";
import { useApp } from "../../context/AppContext";
import PageShell from "../../components/layout/PageShell";
import GlassCard from "../../components/ui/GlassCard";
import Button from "../../components/ui/Button";
import { Input, Select, Toggle } from "../../components/ui/FormFields";

const SettingsPage: React.FC = () => {
  const { settings, setSettings } = useApp();
  const [saved, setSaved] = useState(false);

  const S = (k: keyof typeof settings) => (v: any) => setSettings(s => ({ ...s, [k]: v }));

  const save = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <PageShell title="System Settings" subtitle="Global platform configuration and preferences">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General */}
        <GlassCard className="p-6 space-y-4">
          <h3 className="font-extrabold text-gray-800 text-base border-b border-gray-100 pb-3">General</h3>
          <Input label="Platform Name"  value={settings.siteName}     onChange={S("siteName")}     />
          <Input label="Support Email"  value={settings.supportEmail} onChange={S("supportEmail")} type="email" />
          <Input label="Max Organizations" value={String(settings.maxOrgs)} onChange={v=>S("maxOrgs")(Number(v))} type="number" />
        </GlassCard>

        {/* Assessment */}
        <GlassCard className="p-6 space-y-4">
          <h3 className="font-extrabold text-gray-800 text-base border-b border-gray-100 pb-3">Assessment Defaults</h3>
          <Select label="Default Likert Scale" value={String(settings.defaultScale)} onChange={v=>S("defaultScale")(Number(v))}
            options={[{value:"5",label:"1 – 5 (Standard)"},{value:"7",label:"1 – 7 (Extended)"}]} />
          <Input label="Session Timeout (minutes)" type="number" value={String(settings.sessionTimeout)} onChange={v=>S("sessionTimeout")(Number(v))} />
          <Toggle checked={settings.allowNegative}   onChange={S("allowNegative")}   label="Allow Negative Marking" />
          <Toggle checked={settings.maintenanceMode} onChange={S("maintenanceMode")} label="Maintenance Mode" />
        </GlassCard>

        {/* Scoring reference */}
        <GlassCard className="p-6 lg:col-span-2">
          <h3 className="font-extrabold text-gray-800 text-base border-b border-gray-100 pb-3 mb-4">Scoring Engine Reference</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="p-4 rounded-xl bg-indigo-50">
              <p className="font-bold text-indigo-800 mb-2">Behavioral (Likert)</p>
              <code className="text-xs text-indigo-700 block leading-relaxed">
                Normal:  score = value × weightage<br />
                Reverse: score = (maxScale + 1 − value) × weightage<br />
                Trait %  = (traitScore / maxPossible) × 100
              </code>
            </div>
            <div className="p-4 rounded-xl bg-emerald-50">
              <p className="font-bold text-emerald-800 mb-2">Aptitude (MCQ)</p>
              <code className="text-xs text-emerald-700 block leading-relaxed">
                Correct:   score = weightage<br />
                Incorrect: score = 0 (or −negativeValue)<br />
                Section %  = (sectionScore / maxSection) × 100
              </code>
            </div>
            <div className="p-4 rounded-xl bg-amber-50">
              <p className="font-bold text-amber-800 mb-2">Classification Bands</p>
              <code className="text-xs text-amber-700 block leading-relaxed">
                0 – 40%  → Low<br />
                41 – 70% → Moderate<br />
                71 – 100% → High
              </code>
            </div>
            <div className="p-4 rounded-xl bg-purple-50">
              <p className="font-bold text-purple-800 mb-2">Weightage Rules</p>
              <code className="text-xs text-purple-700 block leading-relaxed">
                Assigned per question only<br />
                Default = 1 (decimal allowed)<br />
                No section / trait-level weightage
              </code>
            </div>
          </div>
        </GlassCard>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={save}>💾 Save Settings</Button>
        {saved && (
          <span className="flex items-center gap-2 text-emerald-600 text-sm font-semibold animate-fade-in">
            ✓ Settings saved successfully!
          </span>
        )}
      </div>
    </PageShell>
  );
};

export default SettingsPage;
